/* eslint-disable react/no-children-prop */
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Collapse, { Panel } from "rc-collapse";
import BookingDurationList from "./BookingDurationList.jsx";
import DurationLabel from "app/pages/.shared/DurationLabel";
import DepartureCityList from "./DepartureCityList.jsx";
import PassengersCounterDisplay from "app/pages/.shared/PassengersCounterDisplay.jsx";
import Loader from "app/pages/.shared/Loader.jsx";
import Period from "app/pages/.shared/Period.jsx";
import AmountContainer from "app/pages/.shared/AmountContainer.jsx";
import { scrollToElement } from "app/utils/scroller";
import findIndex from "lodash/findIndex";
import find from "lodash/find";
import { FormattedMessage } from "react-intl";
import { propTypes, untouch } from "redux-form";
import { BRANDS, OFFER_TYPES, PASSENGER_TYPE, RESOLUTION, STRATEGY } from "app/constants";
import {
	pushTagOnAlternativeDepartureCitySelect,
	pushTagOnDepartureCitySelect,
	pushTagOnDepartureDateSelect,
	pushTagOnDurationSelect,
	pushTagOnOfferTypeSelect,
	pushTagOnPassengerSelect,
} from "app/utils/analytics";
import Popover from "app/pages/.shared/Popover";
import Button from "app/pages/.shared/form/Button";
import PassengersFieldset from "app/pages/PrePackage/components/PassengersFieldset";
import AlternativeDepartureCityList from "app/pages/PrePackage/components/AlternativeDepartureCityList";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import { SearchEngineOffers } from "app/pages/PrePackage/SearchEngineOffers/SearchEngineOffers";
import IconInfo from "app/pages/.shared/static/icons/IconInfo";
import { resolutionPropType } from "app/utils/propTypes";
import Modal from "react-modal";
import AuthLayerMobile from "app/pages/Auth/AuthLayerMobile/AuthLayerMobile";
import PayNow from "app/pages/.shared/PayNow/PayNow";
import PayNowNote from "app/pages/.shared/PayNow/PayNowNote";

import "./search-engine.scss";
import PayNowAlert from "app/pages/.shared/PayNow/PayNowAlert";
import SearchEngineMessagePlaceholderContainer from "app/pages/PrePackage/SearchEngineMessagePlaceholder/SearchEngineMessagePlaceholderContainer";
import AuthModal from "app/pages/Auth/AuthModal/AuthModal";
import loadable from "@loadable/component";
import SidePanel from "app/pages/.shared/SidePanel/SidePanel";
import SearchEngineStepLabel from "app/pages/PrePackage/components/SearchEngineStepLabel/SearchEngineStepLabel";
import FrequencyFlyersMarketing from "@brand/FrequencyFlyersMarketing/FrequencyFlyersMarketing";
import TransportationPictoContainer from "app/pages/.shared/TransportationPicto/TransportationPictoContainer";

const DurationDepartureDateSidePanelContent = loadable(() =>
	import(/* webpackChunkName: "duration-departure-date-side-panel-content" */ "app/pages/PrePackage/components/DatePickerCalendar/DurationDepartureDateSidePanelContent")
);

const DatePickerCalendarModal = loadable(() =>
	import(/* webpackChunkName: "date-picker-calendar-modal" */ "app/pages/PrePackage/components/DatePickerCalendar/DatePickerCalendarModal.jsx")
);

const DatePicker = loadable(() =>
	import(/* webpackChunkName: "datepicker" */ "app/pages/PrePackage/components/DatePicker.jsx")
);

const ACCORDION = {
	OFFERS_PANEL_KEY: "0",
	DEPARTURE_CITIES_PANEL_KEY: "1",
	DURATIONS_DATES_PANEL_KEY: "2",
	PASSENGERS_PANEL_KEY: "3",
};

const mobileModalStyle = {
	overlay: {
		zIndex: 6,
	},
	content: {
		top: "0",
		left: "0",
		right: "0",
		bottom: "0",
		border: "none",
		background: "none",
		overflow: "hidden",
	},
};

class SearchEngine extends React.Component {
	constructor(props) {
		super(props);

		const selectedOfferType = props.booking.offer.type;
		const selectedDepartureDate = props.booking.departureDate;
		const selectedDuration = props.booking.duration;

		this.state = {
			// Par défaut, on veut fermer tous les volets du moteur de recherche si tous les options ont été sélectionnées
			// Sinon on ouvre le vol de l'offre par défaut
			accordionActiveKey:
				selectedOfferType && selectedDepartureDate && selectedDuration
					? -1
					: ACCORDION.OFFERS_PANEL_KEY,
			// Pour hacker le bug de la liste des villes alerantives qui est masqué à cause du overflow:hidden de rc-collapse
			// Voir http://stackoverflow.com/questions/23978084/ui-select-inside-of-accordion
			displayAlternativeCities: false,
			currentPriceIndex: this.getInitialPriceIndex(
				this.props.brand,
				this.props.prices,
				this.props.booking.departureDate
			),
			authModalVisible: false,
			isSidePanelOpen: false,
		};
		this._searchEngineRef = React.createRef();
		this.changeAccordionActivePanel = this.changeAccordionActivePanel.bind(this);
		this.onPrePackageOfferTypeSelection = this.onPrePackageOfferTypeSelection.bind(this);
		this.onPriceSelection = this.onPriceSelection.bind(this);
		this.onDepartureCitySelection = this.onDepartureCitySelection.bind(this);
		this.onMainDepartureCitySelection = this.onMainDepartureCitySelection.bind(this);
		this.onAlternativeDepartureCitySelection = this.onAlternativeDepartureCitySelection.bind(
			this
		);
		this.onDurationSelection = this.onDurationSelection.bind(this);
		this.onAlternativeDurationSelection = this.onAlternativeDurationSelection.bind(this);
		this.onAdultsSelect = this.onAdultsSelect.bind(this);
		this.onInfantsSelect = this.onInfantsSelect.bind(this);
		this.onChildrenSelect = this.onChildrenSelect.bind(this);
		this.onAlternativeCitiesShow = this.onAlternativeCitiesShow.bind(this);
		this.onAlternativeCitiesHide = this.onAlternativeCitiesHide.bind(this);
		this.onChildrenAgeChange = this.onChildrenAgeChange.bind(this);
		this.onInfantsAgeChange = this.onInfantsAgeChange.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
		this.closeAuthModal = this.closeAuthModal.bind(this);
		this.handleOnPanelClose = this.handleOnPanelClose.bind(this);
		this.handleOnPanelOpen = this.handleOnPanelOpen.bind(this);
		this.handleOnModalClose = this.handleOnModalClose.bind(this);
	}

	getInitialPriceIndex(brand, prices, currentDepartureDate) {
		if (!prices || prices.length === 0) {
			return undefined;
		}

		if (currentDepartureDate) {
			return findIndex(prices, price => price.dd === currentDepartureDate);
		}

		const DEFAULT_INITIAL_INDEX = 1; // pas 0 sinon le mois correspondant n'est pas sélectionné dans le calendrier des prix du au fait que le premier élément est un mois

		const bestPriceIndex = findIndex(prices, price => price.i && price.i.mp === true);

		const priceIndex =
			brand === BRANDS.DD || brand === BRANDS.MS ? DEFAULT_INITIAL_INDEX : bestPriceIndex;

		return priceIndex;
	}

	onAdultsSelect(value) {
		this.props.updateBookingPassager(PASSENGER_TYPE.ADULT, value);
		pushTagOnPassengerSelect();
	}

	onInfantsSelect(value) {
		this.props.updateBookingPassager(PASSENGER_TYPE.INFANT, value);
		pushTagOnPassengerSelect();
	}

	onChildrenSelect(value) {
		this.props.updateBookingPassager(PASSENGER_TYPE.CHILD, value);
		pushTagOnPassengerSelect();
	}

	onChildrenAgeChange(birthdate, index) {
		this.props.updateBookingChildrenBirthdate(birthdate, index);
	}

	onInfantsAgeChange(birthdate, index) {
		this.props.updateBookingInfantsBirthdate(birthdate, index);
	}

	onAlternativeCitiesShow() {
		// Pour hacker le bug de la liste des villes alerantives qui est masqué à cause du overflow:hidden de rc-collapse
		// Voir http://stackoverflow.com/questions/23978084/ui-select-inside-of-accordion
		this.setState({
			displayAlternativeCities: true,
		});
	}

	onAlternativeCitiesHide() {
		// Pour hacker le bug de la liste des villes alerantives qui est masqué à cause du overflow:hidden de rc-collapse
		// Voir http://stackoverflow.com/questions/23978084/ui-select-inside-of-accordion
		this.setState({
			displayAlternativeCities: false,
		});
	}

	onPrePackageOfferTypeSelection({ type, label, contractType, config, uri }, negotiated) {
		if (type === OFFER_TYPES.ACCOMODATION_ONLY) {
			this.props.updateBooking({
				offer: { type, label, contractType, uri, negotiated },
				config,
				departureCity: {},
				price: undefined,
				publicPrice: undefined,
			});
			if (this.props.fields.departureCityCode) {
				this.props.fields.departureCityCode.onChange("");
			}
		} else {
			this.props.updateBooking({
				offer: { type, label, contractType, negotiated },
				price: undefined,
				publicPrice: undefined,
				config,
			});
		}

		this.setState(
			{
				accordionActiveKey:
					type === OFFER_TYPES.ACCOMODATION_ONLY
						? ACCORDION.DURATIONS_DATES_PANEL_KEY
						: ACCORDION.DEPARTURE_CITIES_PANEL_KEY,
			},
			() => {
				const offset = this.props.match.path === "/booking/search" ? -20 : -70;

				scrollToElement(this.refs.offer, offset);
				pushTagOnOfferTypeSelect(label);
			}
		);
	}

	onDepartureCitySelection({ code, label, uri }, { isAlternativeDepartureCity }) {
		this.setState(
			{
				accordionActiveKey: ACCORDION.DURATIONS_DATES_PANEL_KEY,
			},
			() => {
				this.props.updateBooking({
					departureCity: { code, label, isAlternative: isAlternativeDepartureCity },
					duration: {},
					departureDate: undefined,
				});
				this.props.fields.duration.onChange("");
				this.props.fields.departureDate.onChange("");
				const offset = this.props.match.path === "/booking/search" ? -20 : -75;

				scrollToElement(this.refs.offer, offset); // scroll pour afficher tout le moteur de recherche

				if (isAlternativeDepartureCity) {
					this.props.fetchPrepackagePricesOfAlternativeCity(
						uri,
						code,
						this.props.booking.offer.type,
						this.props.booking.offer.contractType
					);
				} else {
					this.props.fetchPrepackagePricesOfCity(
						uri,
						code,
						this.props.booking.offer.type,
						this.props.booking.offer.contractType
					);
				}
			}
		);
	}

	onMainDepartureCitySelection({ code, label, uri }) {
		this.onDepartureCitySelection({ code, label, uri }, { isAlternativeDepartureCity: false });
		pushTagOnDepartureCitySelect(label);
	}

	onAlternativeDepartureCitySelection({ code, label, uri }) {
		this.onDepartureCitySelection({ code, label, uri }, { isAlternativeDepartureCity: true });
		pushTagOnAlternativeDepartureCitySelect(label);
	}

	onDurationSelection(durationValue) {
		const { resolution, shop } = this.props;
		const isEnglishMarket = shop === "en-GB";

		const duration = this.props.durations.find(duration => duration.value === durationValue);
		this.props.updateBooking({ duration });

		if (get(this.props.booking, "offer.type") === OFFER_TYPES.ACCOMODATION_ONLY) {
			this.props.fetchPrepackagePricesOfDurations(
				this.props.uriToFetchForAO,
				duration.value,
				this.props.booking.offer.type,
				this.props.booking.offer.contractType
			);
		}

		const isDesktop = resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE;

		// repositionner le moteur de rechercher pour afficher le calendrier en entier pour faciliter la saisie
		let offset = this.props.match.path === "/booking/search" ? -20 : -75;

		if (isEnglishMarket) {
			offset = 0; // pour que le CTA dans la modal calendar soit visible
		}

		const element = isDesktop ? this.refs.duration : this.refs.offer;

		scrollToElement(element, offset);

		pushTagOnDurationSelect(duration.value);

		if (isEnglishMarket) {
			this.handleOnPanelOpen();
		}
	}

	onAlternativeDurationSelection(durationValue) {
		this.props.fields.duration.onChange(durationValue);

		// reset departure date selection when an alternative duration is selected
		this.props.fields.departureDate.onChange("");
		this.props.updateBooking({
			price: undefined,
			publicPrice: undefined,
			departureDate: undefined,
			endDate: undefined,
		});

		this.onDurationSelection(durationValue);
	}

	onPriceSelection(price) {
		const departureDate = price.dd;
		const endDate = price.ed;

		const selectedIndex = this.props.prices.findIndex(price => price.dd === departureDate);

		this.setState(
			{
				accordionActiveKey: ACCORDION.PASSENGERS_PANEL_KEY,
				...(selectedIndex !== -1 && { currentPriceIndex: selectedIndex }),
			},
			() => {
				this.props.updateBooking({
					departureDate: departureDate,
					endDate: endDate,
					price: price.p,
					publicPrice: price.pp,
					hasSpecialOffer: price.so,
				});
				const offset = this.props.match.path === "/booking/search" ? 45 : -75; // valeur 45 pour que le bouton CTA soit visible sur mobile

				scrollToElement(this.refs.offer, offset); // scroll pour afficher tout le moteur de recherche

				pushTagOnDepartureDateSelect(departureDate, endDate);
			}
		);
	}

	changeAccordionActivePanel(activeKey) {
		this.setState({
			accordionActiveKey: activeKey,
		});
	}

	componentDidUpdate(prevProps) {
		const fields = this.props.fields;

		if (!isEqual(prevProps.fields, fields)) {
			const offerTypeValue = get(fields, "offer.value");
			const departureCityValue = get(fields, "departureCityCode.value");
			const durationValue = get(fields, "duration.value");
			const departureDateValue = get(fields, "departureDate.value");

			let defaultAccordionActiveKey;

			if (!offerTypeValue) {
				defaultAccordionActiveKey = ACCORDION.OFFERS_PANEL_KEY;
			} else if (
				!departureCityValue &&
				offerTypeValue === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION
			) {
				defaultAccordionActiveKey = ACCORDION.DEPARTURE_CITIES_PANEL_KEY;
			} else if (
				(offerTypeValue === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION &&
					(!durationValue || !departureDateValue)) ||
				(offerTypeValue === OFFER_TYPES.ACCOMODATION_ONLY &&
					(!durationValue || !departureDateValue))
			) {
				defaultAccordionActiveKey = ACCORDION.DURATIONS_DATES_PANEL_KEY;
			} else {
				/* returning here to avoid the accordion to collapse when no criteria are met */
				return;
			}
			this.setState({ accordionActiveKey: defaultAccordionActiveKey });
		}
	}

	componentWillReceiveProps(nextProps) {
		// Au changement d'offre, on tente de mettre à jour le calendrier de prix si possible
		if (get(this.props.booking, "offer.type") !== nextProps.booking.offer.type) {
			nextProps.syncPrePackageOfferPrices(nextProps);
		}

		// On s'assure d'avoir le bon prix et prix public affiché au dessus du bouton, surtout lorsque l'on change d'offre
		const price = find(nextProps.prices, { dd: nextProps.booking.departureDate });
		if (
			price &&
			(nextProps.booking.price !== price.p ||
				nextProps.booking.publicPrice !== price.pp ||
				nextProps.booking.departureDate !== price.dd ||
				nextProps.booking.endDate !== price.ed)
		) {
			nextProps.updateBooking({
				price: price.p,
				publicPrice: price.pp,
				departureDate: price.dd,
				endDate: price.ed,
				hasSpecialOffer: price.so,
			});
		} else if (price && price.ad) {
			// Si la date de départ actuellement enregistré comporte des durées alternatives dans
			// dans l'actuel liste des prix (suite à un changement de durée par exemple),
			// alors on déselectionne et réinitialise le choix de la date de départ
			nextProps.updateBooking({
				price: undefined,
				publicPrice: undefined,
				departureDate: undefined,
				endDate: undefined,
			});
			nextProps.fields.departureDate.onChange("");
		}

		// Selection de la date de depart initial après avoir récupérer les prix
		if (!this.state.currentPriceIndex && nextProps.prices && nextProps.prices.length > 0) {
			this.setState({
				currentPriceIndex: this.getInitialPriceIndex(
					this.props.brand,
					nextProps.prices,
					price && price.dd
				),
			});
		}
	}

	closeAuthModal() {
		this.setState({ authModalVisible: false });
	}

	handleSubmit(...args) {
		const { handleSubmit, strategy, uuid, resolution } = this.props;
		const onMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

		if (strategy === STRATEGY.PRODUCT_FIRST && !uuid) {
			const [event] = args;
			// note: ici on fait un event.preventDefault() nous même afin d'éviter le rechargement de la page
			event.preventDefault();

			return this.setState(
				{
					authModalVisible: true,
				},
				() => {
					if (onMobile) {
						window.scrollTo(0, 0);
					}
				}
			);
		}
		return handleSubmit(...args);
	}

	handleOnPanelClose() {
		this.setState({
			isSidePanelOpen: false,
			accordionActiveKey: this.props.booking.departureDate
				? ACCORDION.PASSENGERS_PANEL_KEY
				: ACCORDION.DURATIONS_DATES_PANEL_KEY,
		});
	}

	handleOnPanelOpen() {
		this.setState({
			isSidePanelOpen: true,
		});
	}

	handleOnModalClose() {
		this.setState({
			isSidePanelOpen: false,
		});
	}

	render() {
		const {
			resolution,
			fields,
			prePackageOffer,
			submitting,
			allAvailableDurations,
			departureCities,
			outreMerDepartureCities,
			alternativeDepartureCities,
			durations,
			isDepositEnabled,
			isBookingEligibleForDeposit,
			prices,
			months,
			booking,
			mealBasis,
			adultOnly,
			isChildrenAgeRequired,
			productInfo,
			error, // set to undefined on each redux-form/CHANGE
			shop,
			match,
			isDealExpired,
		} = this.props;

		const { isSidePanelOpen } = this.state;

		const isSingleOffer = get(prePackageOffer, "offers.length", 0) === 1;
		const selectedOfferTypeValue = get(booking, "offer.type");
		const selectedDurationValue = get(booking, "duration.value");
		const selectedDepartureCityValue = get(booking, "departureCity.code");
		const selectedDepartureDateValue = get(booking, "departureDate");

		let searchEnginePricesNode = false;
		// On verifie que la durée selectionnée est bien dans la liste des durées disponibles
		if (
			prices.length === 0 &&
			selectedDurationValue &&
			find(durations, { value: selectedDurationValue })
		) {
			searchEnginePricesNode = (
				<div className="search-engine__loader">
					<Loader />
				</div>
			);
		} else if (prices.length !== 0) {
			searchEnginePricesNode = (
				<div className="search-engine__price">
					<DatePicker
						isRental={false}
						productInfo={productInfo}
						offerType={selectedOfferTypeValue}
						initialIndex={this.state.currentPriceIndex}
						months={months}
						prices={prices}
						field={fields.departureDate}
						onPriceChange={this.onPriceSelection}
						selectedDuration={selectedDurationValue}
						onAlternativeDurationChange={this.onAlternativeDurationSelection}
					/>

					{mealBasis && (
						<div className="search-engine__meal-basis">
							<div>
								<IconInfo width={15} height={15} style={{ marginRight: "5px" }} />
							</div>
							<FormattedMessage
								values={{
									meal: mealBasis,
								}}
								id="search.engine.meal.basis"
							/>
						</div>
					)}
				</div>
			);
		}

		let durationDepartureDateNode = (
			<React.Fragment>
				{durations.length === 0 ? (
					<div className="search-engine__loader">
						<Loader />
					</div>
				) : (
					<div>
						<div className="search-engine__duration">
							<BookingDurationList
								offerType={selectedOfferTypeValue}
								productInfo={productInfo}
								amountSuffix={
									<FormattedMessage id="general.amount.per.person.suffix.label.short" />
								}
								durations={durations}
								field={fields.duration}
								onDurationChange={this.onDurationSelection}
							/>
						</div>

						{searchEnginePricesNode}
					</div>
				)}
			</React.Fragment>
		);

		const onMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

		if (shop === "en-GB" && onMobile) {
			durationDepartureDateNode = (
				<React.Fragment>
					{/*<DatePickerCalendarButton onClick={this.handleOnPanelOpen} />*/}

					{durations.length === 0 ? (
						<div className="search-engine__loader">
							<Loader />
						</div>
					) : (
						<React.Fragment>
							<div className="search-engine__duration">
								<BookingDurationList
									offerType={selectedOfferTypeValue}
									productInfo={productInfo}
									amountSuffix={
										<FormattedMessage id="general.amount.per.person.suffix.label.short" />
									}
									durations={durations}
									field={fields.duration}
									onDurationChange={this.onDurationSelection}
									isCollapsable={selectedDepartureDateValue} // si aucune date de depart selectionneée, alors on affiche toutes les durées
								/>
							</div>
						</React.Fragment>
					)}
				</React.Fragment>
			);
		} else if (shop === "en-GB" && !onMobile) {
			durationDepartureDateNode = (
				<React.Fragment>
					{durations.length === 0 ? (
						<div className="search-engine__loader">
							<Loader />
						</div>
					) : (
						<div>
							<div className="search-engine__duration">
								<BookingDurationList
									offerType={selectedOfferTypeValue}
									productInfo={productInfo}
									amountSuffix={
										<FormattedMessage id="general.amount.per.person.suffix.label.short" />
									}
									durations={durations}
									field={fields.duration}
									onDurationChange={this.onDurationSelection}
									isCollapsable
								/>
							</div>

							{isSidePanelOpen && (
								<DatePickerCalendarModal
									show={isSidePanelOpen}
									initialIndex={this.state.currentPriceIndex}
									prices={prices}
									shop={shop}
									hideSidePanel={this.handleOnModalClose}
									fieldDepartureDate={fields.departureDate}
									onPriceChange={this.onPriceSelection}
									selectedDuration={selectedDurationValue}
									onAlternativeDurationChange={
										this.onAlternativeDurationSelection
									}
								/>
							)}
						</div>
					)}
				</React.Fragment>
			);
		}

		// on passe ici par booking au lieu de fields car moins couteux en traitement
		// par contre ca necessite que l'on enregistre la selection du user dans booking a chaque changement
		const offerHeaderLabel = (
			<SearchEngineStepLabel
				ref="offer"
				className="search-engine__header--offer"
				defaultLabel={<FormattedMessage id="search.engine.offers.title" />}
				isComplete={
					get(booking, "offer.label") &&
					this.state.accordionActiveKey !== ACCORDION.OFFERS_PANEL_KEY
				}
				label={
					<div className="search-engine__offer-label">
						{booking.offer.label}
						{booking.offer.type === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION && (
							<TransportationPictoContainer />
						)}
						{booking.offer.type === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION && (
							<FrequencyFlyersMarketing showPictoOnly />
						)}
					</div>
				}
			/>
		);

		const departureCityHeaderLabel = (
			<SearchEngineStepLabel
				ref="departureCity"
				defaultLabel={
					isSingleOffer ? (
						<FormattedMessage id="search.engine.departure.cities.title.transport.with.verb" />
					) : (
						<FormattedMessage id="search.engine.departure.cities.title.transport" />
					)
				}
				isComplete={
					get(booking, "departureCity.label") &&
					this.state.accordionActiveKey !== ACCORDION.DEPARTURE_CITIES_PANEL_KEY
				}
				label={
					<FormattedMessage
						values={{ city: booking.departureCity.label }}
						id="search.engine.departure.city.title.prefix"
					/>
				}
			/>
		);

		const durationHeaderLabel = (
			<SearchEngineStepLabel
				onClick={
					shop === "en-GB" &&
					selectedDepartureDateValue &&
					selectedDurationValue &&
					this.handleOnPanelOpen
				}
				defaultLabel={<FormattedMessage id="search.engine.durations.dates.title" />}
				isComplete={
					selectedDurationValue &&
					this.state.accordionActiveKey !== ACCORDION.DURATIONS_DATES_PANEL_KEY
				}
				ref="duration"
				label={
					<React.Fragment>
						<DurationLabel duration={selectedDurationValue} />

						{selectedDepartureDateValue && (
							<Period
								prefix={<FormattedMessage id="general.period.prefix" />}
								suffix={<FormattedMessage id="general.period.suffix" />}
								fromDate={selectedDepartureDateValue}
								endDate={booking.endDate}
								format={{
									weekday: "short",
									day: "2-digit",
									month: "2-digit",
									year: "numeric",
								}}
							/>
						)}
					</React.Fragment>
				}
			/>
		);

		const passengersHeaderLabel = (
			<SearchEngineStepLabel
				ref="passenger"
				label={
					<PassengersCounterDisplay
						adults={booking.adults}
						infants={booking.infants}
						children={booking.children}
					/>
				}
				defaultLabel={<FormattedMessage id="search.engine.guests.title" />}
				isComplete={
					selectedDepartureDateValue &&
					this.state.accordionActiveKey !== ACCORDION.PASSENGERS_PANEL_KEY
				}
			/>
		);

		const searchEngineClassName = classNames({
			"search-engine__single-offer": true,
			"rc-collapse-item--disabled-filter": isDealExpired,
		});

		const offerClassName = classNames({
			"cy-search-engine-offer-panel": true,
			"rc-collapse-item--disabled-filter": isDealExpired,
		});

		const departureCityClassName = classNames({
			"cy-search-engine-departure-city-panel": true, // la lib rc-collapse nous ne permet pas d'utiliser data-cy
			"rc-collapse-item-overflow-visible": this.state.displayAlternativeCities,
			"rc-collapse-item-disabled": !selectedOfferTypeValue || isDealExpired,
			"rc-collapse-item-hidden":
				selectedOfferTypeValue === OFFER_TYPES.ACCOMODATION_ONLY || isDealExpired,
		});

		const durationClassName = classNames({
			"cy-search-engine-duration-panel": true, // la lib rc-collapse nous ne permet pas d'utiliser data-cy
			"rc-collapse-item-disabled":
				!selectedDepartureCityValue &&
				selectedOfferTypeValue !== OFFER_TYPES.ACCOMODATION_ONLY,
		});

		const passengersClassName = classNames({
			"rc-collapse-item--passengers": true, // la lib rc-collapse nous ne permet pas d'utiliser data-cy
			"cy-search-engine-passenger-panel": true, // la lib rc-collapse nous ne permet pas d'utiliser data-cy
			"rc-collapse-item-disabled":
				booking.offer.type !== OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION
					? !selectedDepartureDateValue
					: !selectedDepartureCityValue || !selectedDepartureDateValue, // attention à ne pas prendre booking.price car le prix est absent pour les départs des villes alternatives
		});

		const footerClassName = classNames("search-engine__footer", {
			"search-engine__footer--hidden":
				booking.offer.type !== OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION
					? !selectedDepartureDateValue
					: !selectedDepartureCityValue || !selectedDepartureDateValue, // attention à ne pas prendre booking.price car le prix est absent pour les départs des villes alternatives
			"search-engine__footer--no-price": !booking.price,
		});

		const isCTADisabled =
			booking.offer.type !== OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION
				? !booking.departureDate
				: !booking.departureCity.code || !booking.departureDate; // attention à ne pas prendre booking.price car le prix est absent pour les départs des villes alternatives

		const payNowNote = booking.price && <PayNowNote total={booking.price} shop={shop} />;

		return (
			<React.Fragment>
				<Modal isOpen={this.state.authModalVisible && onMobile} style={mobileModalStyle}>
					<AuthLayerMobile onAuthSuccess={this.closeAuthModal} />
				</Modal>
				<AuthModal
					isOpen={this.state.authModalVisible && !onMobile}
					onAuthSuccess={this.closeAuthModal}
				/>
				{shop === "en-GB" && onMobile && (
					<SidePanel
						isOpen={isSidePanelOpen}
						onClose={this.handleOnPanelClose}
						direction={"rtl"}
					>
						<DurationDepartureDateSidePanelContent
							offerType={selectedOfferTypeValue}
							durations={durations}
							fieldDuration={fields.duration}
							onDurationChange={this.onDurationSelection}
							isRental={false}
							productInfo={productInfo}
							initialIndex={this.state.currentPriceIndex}
							months={months}
							prices={prices}
							fieldDepartureDate={fields.departureDate}
							onPriceChange={this.onPriceSelection}
							selectedDuration={selectedDurationValue}
							onAlternativeDurationChange={this.onAlternativeDurationSelection}
						/>
					</SidePanel>
				)}
				<form id="search-engine" className="search-engine" onSubmit={this.handleSubmit}>
					<Collapse
						accordion={true}
						onChange={this.changeAccordionActivePanel}
						activeKey={this.state.accordionActiveKey}
					>
						{prePackageOffer.offers.length === 1 ? (
							<div ref="offer" className={searchEngineClassName}>
								<SearchEngineOffers
									prePackageOffer={prePackageOffer}
									allAvailableDurations={allAvailableDurations}
									fields={fields}
									productInfo={productInfo}
									onOfferChange={this.onPrePackageOfferTypeSelection}
								/>
							</div>
						) : (
							<Panel header={offerHeaderLabel} className={offerClassName}>
								<SearchEngineOffers
									prePackageOffer={prePackageOffer}
									allAvailableDurations={allAvailableDurations}
									fields={fields}
									productInfo={productInfo}
									onOfferChange={this.onPrePackageOfferTypeSelection}
								/>
							</Panel>
						)}

						<Panel header={departureCityHeaderLabel} className={departureCityClassName}>
							<div className="search-engine__departure-city">
								{outreMerDepartureCities.length > 0 && (
									<div className="search-engine__subtitle">
										<FormattedMessage id="search.engine.main.departure.cities.title" />
									</div>
								)}
								<DepartureCityList
									productInfo={productInfo}
									departureCities={departureCities}
									field={fields.departureCityCode}
									showCTA={isSingleOffer}
									onDepartureCityChange={this.onMainDepartureCitySelection}
								/>

								{outreMerDepartureCities.length > 0 && (
									<div
										className="search-engine__subtitle"
										style={{ marginTop: "14px" }}
									>
										<FormattedMessage id="search.engine.outre.mer.departure.cities.title" />
									</div>
								)}

								{outreMerDepartureCities.length > 0 && (
									<DepartureCityList
										productInfo={productInfo}
										showCTA={isSingleOffer}
										departureCities={outreMerDepartureCities}
										field={fields.departureCityCode}
										onDepartureCityChange={this.onMainDepartureCitySelection}
									/>
								)}

								{alternativeDepartureCities.length > 0 && (
									<AlternativeDepartureCityList
										openMenuOnFocus={
											this.props.match.path === "/booking/search"
												? false
												: true
										}
										field={fields.departureCityCode}
										cities={alternativeDepartureCities}
										onDepartureCityChange={
											this.onAlternativeDepartureCitySelection
										}
										onMenuOpen={this.onAlternativeCitiesShow}
										onMenuClose={this.onAlternativeCitiesHide}
									/>
								)}
							</div>
						</Panel>

						{/* hack pour eviter que le collapse affiche le bouton avant le side panel*/}
						{shop === "en-GB" &&
						onMobile &&
						selectedDepartureDateValue &&
						selectedDurationValue ? (
							<div className="rc-collapse-item">
								<div
									className="rc-collapse-header"
									data-cy="cy-search-engine-duration-panel"
								>
									<i className="arrow" />
									<div
										className="search-engine__header"
										ref="duration"
										onClick={this.handleOnPanelOpen}
									>
										<div className="search-engine__label">
											<DurationLabel duration={selectedDurationValue} />

											{selectedDepartureDateValue && (
												<Period
													prefix={
														<FormattedMessage id="general.period.prefix" />
													}
													suffix={
														<FormattedMessage id="general.period.suffix" />
													}
													fromDate={selectedDepartureDateValue}
													endDate={booking.endDate}
													format={{
														weekday: "short",
														day: "2-digit",
														month: "2-digit",
														year: "numeric",
													}}
												/>
											)}
										</div>
										<div className="search-engine__edit">
											<FormattedMessage id="search.engine.edit.label" />
										</div>
									</div>
								</div>
							</div>
						) : (
							<Panel header={durationHeaderLabel} className={durationClassName}>
								{durationDepartureDateNode}
							</Panel>
						)}

						<Panel className={passengersClassName} header={passengersHeaderLabel}>
							<PassengersFieldset
								childrenBirthdates={fields.childrenBirthdates}
								infantsBirthdates={fields.infantsBirthdates}
								isChildrenAgeRequired={isChildrenAgeRequired}
								adultOnly={adultOnly}
								adultsField={fields.adults}
								childrenField={fields.children}
								infantsField={fields.infants}
								onAdultsSelect={this.onAdultsSelect}
								onChildrenSelect={this.onChildrenSelect}
								onInfantsSelect={this.onInfantsSelect}
								onChildrenAgeChange={this.onChildrenAgeChange}
								onInfantsAgeChange={this.onInfantsAgeChange}
							/>
						</Panel>
					</Collapse>

					<div className={footerClassName}>
						{booking.price && (
							<div className="search-engine__selected-price">
								<AmountContainer
									amount={booking.price}
									suffix={
										<span>
											<FormattedMessage id="general.amount.per.person.suffix.label" />
											<span>*</span>
										</span>
									}
								/>
							</div>
						)}

						{booking.publicPrice &&
							selectedOfferTypeValue !== OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION && (
								<div className="search-engine__selected-price search-engine__selected-price--public">
									<AmountContainer
										amount={booking.publicPrice}
										prefix={<FormattedMessage id="general.instead.of.price" />}
									/>
								</div>
							)}

						{booking.price && (
							<div className="search-engine__note">
								<span className="search-engine__note-asterique">*</span>
								<FormattedMessage id="search.engine.price.note" />

								{productInfo.ul === true &&
									selectedOfferTypeValue ===
										OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION && (
										<FormattedMessage id="search.engine.price.note.business">
											{message => (
												<div
													style={{
														marginLeft: 5,
														display: "inline-block",
													}}
												>
													{message}
												</div>
											)}
										</FormattedMessage>
									)}
							</div>
						)}
						{isDepositEnabled && !isBookingEligibleForDeposit && (
							<div className="search-engine__alert">
								<PayNowAlert shop={shop} />
							</div>
						)}
					</div>
					<div className="search-engine__action-button">
						{isBookingEligibleForDeposit &&
							(match.path === "/booking/search" ? (
								<PayNow
									total={booking.price}
									shop={shop}
									withDepositIcon
									withSuffix
								/>
							) : (
								payNowNote
							))}

						<div className="search-engine__action">
							<Button
								submit
								disabled={isCTADisabled}
								design="primary"
								loading={submitting}
								data-cy="search-engine-button"
							>
								<FormattedMessage id="search.engine.book.now" />
							</Button>
							<Popover
								onClose={() => {
									untouch(
										"booking-search-form",
										"offer",
										"departureCityCode",
										"departureDate",
										"duration",
										"adults",
										"children",
										"infants"
									);
								}}
								isOpen={error}
							>
								{error ? <FormattedMessage {...error} /> : false}
							</Popover>
							<div className="search-engine__marketing-placeholder">
								<SearchEngineMessagePlaceholderContainer />
							</div>
						</div>
					</div>
				</form>
			</React.Fragment>
		);
	}
}

SearchEngine.defaultProps = {
	prePackageOffer: {},
	departureCities: [],
	outreMerDepartureCities: [],
	alternativeDepartureCities: [],
	durations: [],
	prices: [],
	months: [],
	booking: {},
	productInfo: {},
};

SearchEngine.propTypes = {
	...propTypes,
	uriToFetchForAO: PropTypes.string,
	uriToFetchForFA: PropTypes.string,
	allAvailableDurations: PropTypes.arrayOf(PropTypes.number),
	resolution: resolutionPropType,
	strategy: PropTypes.string,
	departureCities: PropTypes.array,
	outreMerDepartureCities: PropTypes.array,
	alternativeDepartureCities: PropTypes.array,
	durations: PropTypes.array,
	offers: PropTypes.array,
	prices: PropTypes.array,
	months: PropTypes.array,
	booking: PropTypes.object,
	adultOnly: PropTypes.bool,
	isChildrenAgeRequired: PropTypes.bool,
	syncPrePackageOfferPrices: PropTypes.func,
	updateBooking: PropTypes.func,
	updateBookingChildrenBirthdates: PropTypes.func,
	updateBookingInfantsBirthdate: PropTypes.func,
	productInfo: PropTypes.object,
	isDealExpired: PropTypes.bool,
};

export default SearchEngine;
